import {
  bool, func, node, object, oneOfType, shape, string, arrayOf,
} from 'prop-types';
import React, { isValidElement, useEffect, useState } from 'react';

import { isObject } from '@ezugi/utils';

import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { createPortalComponent } from '../Portal/Portal';
import {
  Container, Wrapper, StyledDescription, StyledImage, StyledTitle, ButtonsContainer,
} from './style';

// We create a specific container (div#dialog-container) for all the dialog prompts.
const Portal = createPortalComponent('dialog-container');

const buttonType = shape({
  label: string.isRequired,
  background: string.isRequired,
  foreground: string.isRequired,
  handler: func,
});

const Dialog = ({ on, children, style, className, onClose, buttonList, ...props }) => {
  const [ showPortal, setShowPortal ] = useState(on);

  const togglePortal = (show) => {
    // on remove call callback prop onClose received from parent component
    !show && onClose && onClose();

    setShowPortal(show);
  };

  useEffect(() => {
    togglePortal(on);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ on ]);

  const closeDialog = () => togglePortal(false);

  const showDialog = () => {
    togglePortal(true);
  };

  const getClickHandler = ({ handler }) => handler || closeDialog;

  const renderButtons = () => buttonList.map((button, index) => {
    const { label, ...buttonProps } = button;

    return (
      <Button key={`${label}_${index}`} onClick={getClickHandler(button)} {...buttonProps}>
        {label}
      </Button>
    );
  });

  if (!showPortal) {
    return null;
  }

  return (
    <Portal>
      <Container data-name="dialog-container" style={{ ...style }} className={className}>
        {children ? (
          typeof children === 'function' ? children({ showDialog, closeDialog }) : children
        ) : (
          <Wrapper>
            {!props.icon && (
              <StyledImage>
                {isValidElement(props.icon)
                  ? props.icon
                  : props.icon && <Icon size={24} {...(isObject(props.icon) ? props.icon : { svg: props.icon })} />}
              </StyledImage>
            )}
            {!!props.title && (
              <StyledTitle data-e2e="dialog-title">
                {props.title}
              </StyledTitle>
            )}
            {!!props.description && (
              <StyledDescription data-e2e="dialog-description">
                {props.description}
              </StyledDescription>
            )}
            {!!props.buttonList && (
              <ButtonsContainer data-e2e="dialog-buttons">
                {renderButtons()}
              </ButtonsContainer>
            )}
          </Wrapper>
        )}
      </Container>
    </Portal>
  );
};

Dialog.Wrapper = Wrapper;
Dialog.Image = StyledImage;
Dialog.Title = StyledTitle;
Dialog.Description = StyledDescription;
Dialog.Buttons = ButtonsContainer;
Dialog.Button = Button;

Dialog.propTypes = {
  on: bool.isRequired,
  style: object,
  icon: oneOfType([ string, object, node ]),
  title: string,
  description: string,
  buttonList: arrayOf(buttonType),
  onClose: func,
};

Dialog.defaultProps = {
  buttonList: [],
};

export default Dialog;
